import React from "react";
import { useLocation } from "react-router-dom";

// Where each page was opened from, so a Back button can return there instead of to a fixed list.
//
// Keyed by the history entry (location.key), not kept as one "previous page": after going
// Email → Lead A → Lead B and pressing Back, Lead A must still know it came from Email.
// A page that only changes its own query string (tabs, filters) keeps its original origin.

const cameFrom = new Map<string, string | null>();
let lastKey: string | null = null;
let lastPath: string | null = null;

const pathOnly = (p: string | null): string | null => (p ? p.split('?')[0] : null);

/** The origin for this history entry. Before the tracker has seen it, it's wherever we just were. */
const resolve = (key: string, pathname: string): string | null => {
    if (cameFrom.has(key)) return cameFrom.get(key) ?? null;
    // Same page, new query string: inherit where that page came from.
    if (pathOnly(lastPath) === pathname && lastKey) return cameFrom.get(lastKey) ?? null;
    return lastPath;
};

// #region useTrackRoute
/** Records every navigation. Mounted once, in the app header, which is on every page. */
export const useTrackRoute = (): void => {
    const { key, pathname, search } = useLocation();
    React.useEffect(() => {
        if (!cameFrom.has(key)) cameFrom.set(key, resolve(key, pathname));
        lastKey = key;
        lastPath = pathname + search;
    }, [key, pathname, search]);
};

// #region useCameFrom
/** The in-app path (with its query string) this page was opened from; null after a fresh load or a typed URL. */
export const useCameFrom = (): string | null => {
    const { key, pathname } = useLocation();
    const from = resolve(key, pathname);
    return pathOnly(from) === pathname ? null : from;
};
